// src/pages/veci/CuentaVeci.js
//
// Perfil del veci conectado al backend. Tres pestañas:
//  - Perfil: nombre, apellido, apodo, dirección, foto, editar.
//  - Fiados: lista de fiados con estados y acciones (confirmar, reclamar).
//  - Cuenta: cambiar contraseña, dar de baja.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Stack, Card, CardContent, Tab, Tabs, Avatar, Button,
  TextField, Grid, Chip, Alert, CircularProgress, Divider, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, LinearProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import EditIcon         from '@mui/icons-material/Edit';
import PhotoCameraIcon  from '@mui/icons-material/PhotoCamera';
import DeleteIcon       from '@mui/icons-material/Delete';
import LogoutIcon       from '@mui/icons-material/Logout';
import CheckCircleIcon  from '@mui/icons-material/CheckCircle';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';

import { useApp } from '../../context/AppContext';
import { perfilVeci, cuenta, passwords } from '../../api/perfilApi';
import { fiados as apiFiados } from '../../api/fiadosApi';
import SelectorMunicipio from '../../components/registro/SelectorMunicipio';
import { formatoCOP } from '../../utils/format';

export default function CuentaVeci() {
  const [tab, setTab] = useState(0);

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="overline" color="text.secondary">Mi cuenta</Typography>
        <Typography variant="h4" sx={{ fontFamily: 'Fraunces' }}>Tu cuenta</Typography>
      </Box>

      <Card>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="Perfil" />
          <Tab label="Mis fiados" />
          <Tab label="Cuenta y seguridad" />
        </Tabs>
      </Card>

      {tab === 0 && <TabPerfil />}
      {tab === 1 && <TabFiados />}
      {tab === 2 && <TabCuenta />}
    </Stack>
  );
}

// ═══════════════════════════════════════════════════════════
// TAB 1: PERFIL
// ═══════════════════════════════════════════════════════════

function TabPerfil() {
  const [perfil, setPerfil]     = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError]       = useState('');
  const [ok, setOk]             = useState('');
  const [editando, setEdit]     = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [subiendoFoto, setSubiendoFoto] = useState(false);
  const [borrador, setBorrador] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true); setError('');
    try {
      const r = await perfilVeci.obtener();
      setPerfil(r);
      setBorrador(r);
    } catch (e) {
      setError(e.mensajeUsuario || 'No se pudo cargar tu perfil.');
    } finally { setCargando(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const upd = (k) => (e) => setBorrador(s => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const guardar = async () => {
    setError(''); setOk(''); setGuardando(true);
    try {
      const payload = {
        nombre:         borrador.nombre || borrador.Nombre,
        apellido:       borrador.apellido || borrador.Apellido,
        apodo:          borrador.apodo || null,
        direccion:      borrador.direccion || borrador.Direccion,
        detalleEntrega: borrador.detalleEntrega || null,
        barrio:         borrador.barrio || null,
        telefono:       borrador.telefono || borrador.Telefono,
        latitud:        borrador.latitud ?? null,
        longitud:       borrador.longitud ?? null,
        municipioID:    parseInt(borrador.municipioID || borrador.MunicipioID, 10),
      };
      const r = await perfilVeci.actualizar(payload);
      setPerfil(r);
      setBorrador(r);
      setEdit(false);
      setOk('Perfil actualizado.');
    } catch (e) {
      setError(e.mensajeUsuario || 'No se pudo actualizar.');
    } finally { setGuardando(false); }
  };

  const cambiarFoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSubiendoFoto(true); setError('');
    try {
      await perfilVeci.subirFoto(file);
      const r = await perfilVeci.obtener();
      setPerfil(r); setBorrador(r);
      setOk('Foto actualizada.');
    } catch (er) {
      setError(er.message || 'No se pudo subir la foto.');
    } finally { setSubiendoFoto(false); }
  };

  const eliminarFoto = async () => {
    setError('');
    try {
      await perfilVeci.eliminarFoto();
      const r = await perfilVeci.obtener();
      setPerfil(r); setBorrador(r);
      setOk('Foto eliminada.');
    } catch (er) {
      setError(er.mensajeUsuario || 'No se pudo eliminar.');
    }
  };

  if (cargando) return <CircularProgress />;
  if (!perfil) return <Alert severity="error">{error || 'No se pudo cargar el perfil.'}</Alert>;

  const fotoUrl = perfil.fotoUrl || perfil.FotoUrl;
  const nombreCompleto = perfil.nombreCompleto || perfil.NombreCompleto;

  return (
    <Card>
      <CardContent>
        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
        {ok && <Alert severity="success" onClose={() => setOk('')} sx={{ mb: 2 }}>{ok}</Alert>}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3, flexWrap: 'wrap' }}>
          <Box sx={{ position: 'relative' }}>
            <Avatar sx={{ width: 96, height: 96, bgcolor: 'primary.main', fontSize: 36 }} src={fotoUrl || undefined}>
              {(nombreCompleto || 'V').charAt(0)}
            </Avatar>
            {subiendoFoto && (
              <CircularProgress
                sx={{ position: 'absolute', top: 0, left: 0 }}
                size={96}
              />
            )}
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontFamily: 'Fraunces' }}>{nombreCompleto}</Typography>
            <Typography variant="body2" color="text.secondary">{perfil.correo || perfil.Correo}</Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Button component="label" size="small" startIcon={<PhotoCameraIcon />}>
                Cambiar foto
                <input hidden type="file" accept="image/*" onChange={cambiarFoto} />
              </Button>
              {fotoUrl && (
                <Button size="small" color="inherit" onClick={eliminarFoto}>Quitar</Button>
              )}
            </Stack>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {!editando ? (
          <>
            <Grid container spacing={2}>
              <Info label="Nombre" value={perfil.nombre || perfil.Nombre} />
              <Info label="Apellido" value={perfil.apellido || perfil.Apellido} />
              <Info label="Apodo" value={perfil.apodo || '—'} />
              <Info label="Teléfono" value={perfil.telefono || perfil.Telefono} />
              <Info label="Correo" value={perfil.correo || perfil.Correo} />
              <Info label="Municipio" value={perfil.municipio || perfil.Municipio || '—'} />
              <Info label="Dirección" value={perfil.direccion || perfil.Direccion} full />
              <Info label="Detalle (torre, apto)" value={perfil.detalleEntrega || '—'} />
              <Info label="Barrio o conjunto" value={perfil.barrio || '—'} />
            </Grid>
            <Button
              startIcon={<EditIcon />}
              variant="outlined"
              sx={{ mt: 3 }}
              onClick={() => setEdit(true)}
            >
              Editar
            </Button>
          </>
        ) : (
          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Nombre" value={borrador.nombre || ''} onChange={upd('nombre')} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Apellido" value={borrador.apellido || ''} onChange={upd('apellido')} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Apodo" value={borrador.apodo || ''} onChange={upd('apodo')} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Teléfono" value={borrador.telefono || ''} onChange={upd('telefono')} />
              </Grid>
            </Grid>
            <SelectorMunicipio
              value={borrador.municipioID || borrador.MunicipioID}
              onChange={(v) => setBorrador(s => ({ ...s, municipioID: v }))}
            />
            <TextField fullWidth label="Dirección" value={borrador.direccion || ''} onChange={upd('direccion')} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Detalle" value={borrador.detalleEntrega || ''} onChange={upd('detalleEntrega')} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Barrio o conjunto" value={borrador.barrio || ''} onChange={upd('barrio')} />
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Button onClick={() => { setBorrador(perfil); setEdit(false); }}>Cancelar</Button>
              <Button variant="contained" onClick={guardar} disabled={guardando}>
                {guardando ? 'Guardando…' : 'Guardar cambios'}
              </Button>
            </Box>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

function Info({ label, value, full = false }) {
  return (
    <Grid item xs={12} md={full ? 12 : 6}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body1" sx={{ fontWeight: 500 }}>{value}</Typography>
    </Grid>
  );
}

// ═══════════════════════════════════════════════════════════
// TAB 2: FIADOS
// ═══════════════════════════════════════════════════════════

function TabFiados() {
  const [items, setItems]       = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError]       = useState('');
  const [reclamando, setReclamando] = useState(null); // { id, motivo }

  const cargar = useCallback(async () => {
    setCargando(true); setError('');
    try {
      const r = await apiFiados.listar();
      setItems(Array.isArray(r) ? r : (r.items || []));
    } catch (e) {
      setError(e.mensajeUsuario || 'No se pudieron cargar los fiados.');
    } finally { setCargando(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const confirmar = async (id) => {
    try {
      await apiFiados.confirmar(id);
      cargar();
    } catch (e) { setError(e.mensajeUsuario || 'No se pudo confirmar.'); }
  };

  const enviarReclamo = async () => {
    try {
      await apiFiados.reclamar(reclamando.id, reclamando.motivo || '');
      setReclamando(null);
      cargar();
    } catch (e) { setError(e.mensajeUsuario || 'No se pudo enviar el reclamo.'); }
  };

  if (cargando) return <CircularProgress />;

  return (
    <Card>
      <CardContent>
        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

        {items.length === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No tienes fiados registrados.
          </Typography>
        ) : (
          <Stack spacing={2}>
            {items.map((f) => {
              const id = f.id || f.Id;
              const negocio = f.negocioNombre || f.NegocioNombre;
              const original = f.valorOriginal ?? f.ValorOriginal;
              const abonado  = f.totalAbonado ?? f.TotalAbonado ?? 0;
              const saldo    = f.saldoPendiente ?? f.SaldoPendiente ?? 0;
              const estado   = f.estado || f.Estado;
              const conf     = f.estadoConfirmacion || f.EstadoConfirmacion;
              const desc     = f.descripcion || f.Descripcion;
              const progreso = original > 0 ? Math.round((abonado / original) * 100) : 0;

              return (
                <Card key={id} variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 1 }}>
                      <Box>
                        <Typography variant="h6" sx={{ fontFamily: 'Fraunces' }}>{negocio}</Typography>
                        {desc && <Typography variant="caption" color="text.secondary">{desc}</Typography>}
                      </Box>
                      <Stack direction="row" spacing={1}>
                        <Chip label={estado} size="small" color={estado === 'VIGENTE' ? 'warning' : estado === 'PAGADO' ? 'success' : 'default'} />
                        {conf === 'pendiente_confirmacion' && (
                          <Chip label="Confirmación pendiente" size="small" color="info" />
                        )}
                      </Stack>
                    </Box>

                    <LinearProgress variant="determinate" value={progreso} sx={{ my: 1, height: 8, borderRadius: 4 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">
                        Abonado {formatoCOP(abonado)} de {formatoCOP(original)}
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        Debes {formatoCOP(saldo)}
                      </Typography>
                    </Box>

                    {conf === 'pendiente_confirmacion' && (
                      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                        <Button
                          size="small" variant="contained" color="success"
                          startIcon={<CheckCircleIcon />}
                          onClick={() => confirmar(id)}
                        >
                          Confirmar
                        </Button>
                        <Button
                          size="small" variant="outlined" color="error"
                          startIcon={<ReportProblemIcon />}
                          onClick={() => setReclamando({ id, motivo: '' })}
                        >
                          No es mío
                        </Button>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        )}
      </CardContent>

      <Dialog open={!!reclamando} onClose={() => setReclamando(null)} fullWidth maxWidth="sm">
        <DialogTitle>Reclamar fiado</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Explica al negocio por qué este fiado no corresponde. La tienda lo revisará.
          </Typography>
          <TextField
            fullWidth multiline minRows={3} label="Motivo del reclamo"
            value={reclamando?.motivo || ''}
            onChange={(e) => setReclamando(s => ({ ...s, motivo: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReclamando(null)}>Cancelar</Button>
          <Button onClick={enviarReclamo} variant="contained" color="error">Enviar reclamo</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════
// TAB 3: CUENTA (password + dar de baja)
// ═══════════════════════════════════════════════════════════

function TabCuenta() {
  const { cerrarSesionBackend } = useApp();
  const navigate = useNavigate();
  const [passActual, setActual]   = useState('');
  const [passNueva, setNueva]     = useState('');
  const [passRepetir, setRepetir] = useState('');
  const [cambiando, setCambiando] = useState(false);
  const [error, setError]         = useState('');
  const [ok, setOk]               = useState('');

  const [bajaAbierto, setBaja] = useState(false);
  const [bajaMotivo, setBajaMotivo] = useState('');
  const [bajando, setBajando] = useState(false);

  const cambiarPassword = async () => {
    setError(''); setOk('');
    if (passNueva.length < 6) { setError('La nueva contraseña debe tener al menos 6 caracteres.'); return; }
    if (passNueva !== passRepetir) { setError('Las contraseñas no coinciden.'); return; }
    setCambiando(true);
    try {
      await passwords.cambiar({ passwordActual: passActual, passwordNueva: passNueva });
      setActual(''); setNueva(''); setRepetir('');
      setOk('Contraseña actualizada.');
    } catch (e) {
      setError(e.mensajeUsuario || 'No se pudo cambiar la contraseña.');
    } finally { setCambiando(false); }
  };

  const confirmarBaja = async () => {
    setBajando(true);
    try {
      await cuenta.darDeBajaVeci({ motivo: bajaMotivo || null });
      await cerrarSesionBackend();
      navigate('/login', { replace: true });
    } catch (e) {
      setError(e.mensajeUsuario || 'No se pudo dar de baja la cuenta.');
      setBajando(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontFamily: 'Fraunces' }}>Cambiar contraseña</Typography>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          {ok && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setOk('')}>{ok}</Alert>}

          <Stack spacing={2}>
            <TextField type="password" label="Contraseña actual" value={passActual} onChange={(e) => setActual(e.target.value)} />
            <TextField type="password" label="Nueva contraseña" value={passNueva} onChange={(e) => setNueva(e.target.value)} />
            <TextField type="password" label="Repetir nueva" value={passRepetir} onChange={(e) => setRepetir(e.target.value)} />
            <Button variant="contained" onClick={cambiarPassword} disabled={cambiando}>
              {cambiando ? 'Guardando…' : 'Cambiar contraseña'}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ borderColor: 'error.main' }}>
        <CardContent>
          <Typography variant="h6" color="error" sx={{ mb: 1, fontFamily: 'Fraunces' }}>Zona de peligro</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Puedes dar de baja tu cuenta. Se mantendrá desactivada 60 días, tiempo en el que puedes reactivarla iniciando sesión con el mismo teléfono. Después de esos 60 días se anonimiza y no se puede recuperar.
          </Typography>
          <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => setBaja(true)}>
            Dar de baja mi cuenta
          </Button>
        </CardContent>
      </Card>

      <Dialog open={bajaAbierto} onClose={() => !bajando && setBaja(false)} fullWidth maxWidth="sm">
        <DialogTitle>Dar de baja tu cuenta</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Tendrás 60 días para reactivarla iniciando sesión. Después de eso se anonimiza.
          </Alert>
          <TextField
            fullWidth multiline minRows={2} label="¿Por qué te vas? (opcional)"
            value={bajaMotivo}
            onChange={(e) => setBajaMotivo(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBaja(false)} disabled={bajando}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={confirmarBaja} disabled={bajando}>
            {bajando ? 'Dando de baja…' : 'Sí, dar de baja'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
